import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import pluralize from 'pluralize';
import { filter, take } from 'rxjs/operators';
import { FAKE_CHECK_OUT_QTY_FIELD, FAKE_QUANTITY_ON_HAND_FIELD } from '../../../../../shared/fields/fake-fields';
import { STANDARD_FIELDS } from '../../../../../shared/fields/standard-fields';
import * as fromRoot from '../../../core/store';
import * as assetEventActions from '../../../core/store/asset-events/asset-event.actions';
import * as layoutActions from '../../../core/store/layout/layout.actions';
import { Asset, AssetEvent } from '../../../core/store/models';
import { assetEventSelectors } from '../../../core/store/selectors';
import { ActionStatus, SavingMessage, Severity } from '../../../core/store/types';
import * as assetActions from '../../../core/store/assets/asset.actions';
export interface CheckOutModalData {
    assetEventsForAssetId: { [assetId: number]: AssetEvent };
    assets: Asset[];
    eventId: number;
    qtyOnHand: { [assetId: number]: number };
}

@Component({
    templateUrl: './check-out-modal.component.html',
    styles: [`:host ::ng-deep .mdc-dialog__title+.mdc-dialog__content { padding: 24px;}
              :host ::ng-deep .mdc-text-field--filled.mdc-text-field--disabled { background-color: unset;}`],
    standalone: false
})
export class CheckOutModalComponent implements OnInit {
    assetEventsForAssetId: { [assetId: number]: AssetEvent };
    assets: Asset[];
    checkOutQtyField = FAKE_CHECK_OUT_QTY_FIELD;
    eventId: number;
    modalForm: UntypedFormGroup[] = [];
    nameField = STANDARD_FIELDS.name;
    qtyOnHand: { [assetId: number]: number };
    quantityOnHandField = FAKE_QUANTITY_ON_HAND_FIELD;
    saveDisabled = false;
    savingMessage?: SavingMessage[] = [];

    constructor(@Inject(MAT_DIALOG_DATA) private data: CheckOutModalData, public dialogRef: MatDialogRef<CheckOutModalComponent>, private fb: UntypedFormBuilder, private store: Store<fromRoot.State>) { }

    ngOnInit() {
        this.assets = this.data.assets;
        this.assetEventsForAssetId = this.data.assetEventsForAssetId;
        this.eventId = this.data.eventId;
        this.qtyOnHand = this.data.qtyOnHand;
        this.buildForm();
    }

    buildForm() {
        this.assets.forEach(asset => {
            const formObj = {};
            formObj[this.nameField.canonical_name] = [{ value: asset.name, disabled: true }];
            formObj[this.quantityOnHandField.canonical_name] = [{ value: this.qtyOnHand[asset.id], disabled: true }];
            formObj[this.checkOutQtyField.canonical_name] = [
                { value: this.qtyOnHand[asset.id], disabled: false },
                [Validators.min(0), Validators.max(this.qtyOnHand[asset.id])]
            ];
            this.modalForm[asset.id] = this.fb.group(formObj);
        });
    }

    // Checks that the check out quantity is not negative or greater than the quantity on hand
    validateCheckOutQtyValues() {
        let isValid = true;
        this.assets.forEach(asset => {
            const values = this.modalForm[asset.id].getRawValue();
            const qtyOnHand = this.qtyOnHand[asset.id];
            const checkOutQty = values[this.checkOutQtyField.canonical_name] ? Number(values[this.checkOutQtyField.canonical_name]) : 0;
            if (checkOutQty < 0 || checkOutQty > qtyOnHand || !Number.isInteger(checkOutQty)) {
                isValid = false;
            }
        });
        return isValid;
    }

    save() {
        if (this.validateCheckOutQtyValues()) {
            this.savingMessage = [{ severity: Severity.Info, summary: 'Saving...' }];
            const newAssetEvents = [];
            this.assets.forEach(asset => {
                const values = this.modalForm[asset.id].getRawValue();
                const newAssetEvent = new AssetEvent(this.assetEventsForAssetId[asset.id]);
                const checkOutQty = values[this.checkOutQtyField.canonical_name] ? Number(values[this.checkOutQtyField.canonical_name]) : 0;
                newAssetEvent.qty_on_loan = newAssetEvent.qty_on_loan + checkOutQty;
                newAssetEvent.check_out_qty = checkOutQty;
                newAssetEvents.push(newAssetEvent);
            });
            this.store.dispatch(new assetEventActions.UpdateMultipleAction({ event_id: this.eventId, assetEvents: newAssetEvents }));

            this.store.select(assetEventSelectors.getAssetEventSaving).pipe(
                filter(loading => loading !== ActionStatus.Loading),
                take(1))
                .subscribe((loading) => {
                    if (loading === ActionStatus.Complete) {
                        const message = `${pluralize('Asset', this.assets.length)} ${pluralize('has', this.assets.length)} been updated.`;
                        this.store.dispatch(new layoutActions.InfoAction({ message }));
                        this.store.dispatch(new assetActions.UpdateAction({ assets: this.assets }));
                        this.dialogRef.close();
                    } else {
                        this.savingMessage = [];
                    }
                });
        } else {
            this.savingMessage = [{ severity: Severity.Warn, summary: 'Check out quantity must be an integer greater than 0 and less than the quantity on hand' }];
        }
    }
}
